import type { CycloneCommunity } from "@voxmedia/duet-context/context/community-context";
import type { CommunityConfig } from "@voxmedia/duet-context/context/config-context";

import {
  NETWORK_COMMUNITY_DATA,
  COMMUNITY_SHORTNAMES,
  VOX_CREATIVE_COMMUNITY_ID,
  getNetworkPrimaryCommunityId,
} from "./communities";

const getVoxMediaCachePrefix = (siteId: string | number) => {
  const targetEnv = process.env.NEXT_PUBLIC_DUET_ENV_TARGET || "production";
  let envPrefix = "";

  if (targetEnv !== "production") {
    // Sanitize non-alphanumeric characters from env (e.g. sandbox-1 -> sandbox1)
    const sanitizedEnv = targetEnv.replace(/[^a-zA-Z0-9]/g, "");
    /**
     * Use first two characters and last character of sanitized env
     * e.g. qa1 -> qaa1, sandbox1 -> sax1, release -> rese
     */
    envPrefix = `${sanitizedEnv.slice(0, 2)}${sanitizedEnv.slice(-2)}`;
  }

  return `${process.env.NEXT_PUBLIC_NETWORK_PREFIX}${siteId}${envPrefix}:`;
};

interface TargetlessCommunityConfig {
  communityName: string;
  communitySailthruSlug?: string;
  communityShortName: string;
  domain: string;
  isPrimaryCommunity: boolean;
  vipDomain: string;
  wpSiteId: number;
  cycloneCommunities?: Record<string, CycloneCommunity>;
  primaryCommunityId: number;
  isFeedOnlyCommunity?: boolean | undefined;
}

// Function to get targetless community config
export const getTargetlessCommunityConfig = (
  communityId: number,
): TargetlessCommunityConfig => {
  // Find the network that contains this community
  const network = Object.entries(NETWORK_COMMUNITY_DATA).find(
    ([, network]) => communityId in network.communities,
  );

  if (!network) {
    throw new Error(
      `No community config found for communityId: ${communityId}`,
    );
  }

  const [networkIdStr, networkConfig] = network;
  const networkId = parseInt(networkIdStr);
  const community = networkConfig.communities[communityId];

  if (!community) {
    throw new Error(`Invalid community: ${communityId}`);
  }

  const {
    name,
    domain,
    sailthruSlug,
    subdomain,
    wpSiteId,
    isPrimaryCommunity = false,
    cycloneCommunities,
    isFeedOnlyCommunity = false,
  } = community;
  const communityShortName = COMMUNITY_SHORTNAMES[communityId] || name;

  // Get the primary community ID for this network
  const primaryCommunityId = getNetworkPrimaryCommunityId(networkId);

  return {
    communityName: name,
    communitySailthruSlug: sailthruSlug,
    communityShortName,
    // Only add www. for non-subdomain format communities
    domain: !subdomain ? `www.${domain}` : domain,
    vipDomain: domain,
    isPrimaryCommunity,
    wpSiteId,
    cycloneCommunities,
    primaryCommunityId,
    isFeedOnlyCommunity,
  };
};

export const getVipDomains = ({
  targetEnv,
  vipDomain,
  fastlyProxySubdomain,
  isPrimaryCommunity,
}: {
  targetEnv: string;
  vipDomain: string;
  fastlyProxySubdomain: string | null;
  isPrimaryCommunity: boolean;
}) => {
  const isProduction = targetEnv === "production";
  const isLocal = targetEnv === "local";

  const isSubdomainFormatSubcommunity =
    !isPrimaryCommunity && vipDomain.split(".").length === 3;

  const vipCommunityDomain = !isProduction
    ? `${targetEnv}.${vipDomain}`
    : !isSubdomainFormatSubcommunity
      ? `www.${vipDomain}`
      : // Subcommunities using subdomains won't be prefixed with www
        vipDomain;

  let vipCommunityApiDomain = isProduction
    ? `platform.${vipDomain}`
    : `platform.${targetEnv}.${vipDomain}`;

  if (isLocal) {
    const network = vipDomain.replace(".com", "");
    vipCommunityApiDomain = `platform.${network}.voxmedia.wpenv.net`;
  }

  if (fastlyProxySubdomain) {
    if (isSubdomainFormatSubcommunity) {
      /**
       * On the production site, we don't want to prefix the domain with the fastly proxy subdomain
       * because the sub-community subdomain replaces the www subdomain and it is already included
       * in vipDomain.
       */
      if (fastlyProxySubdomain === "www") {
        vipCommunityApiDomain = vipDomain;
      } else {
        // Split domain into segments: ["austin", "eater", "com"]
        const [subdomain, ...rest] = vipDomain.split(".");

        // Reconstruct with fastly proxy subdomain: "austin.staging.eater.com"
        vipCommunityApiDomain = `${subdomain}.${fastlyProxySubdomain}.${rest.join(".")}`;
      }
    } else {
      // Primary community: directly prefix with fastly proxy subdomain
      vipCommunityApiDomain = `${fastlyProxySubdomain}.${vipDomain}`;
    }
  }

  return {
    vipCommunityDomain,
    vipCommunityApiDomain,
  };
};

export const getCommunityConfig = (communityId: number): CommunityConfig => {
  const targetEnv = process.env.NEXT_PUBLIC_DUET_ENV_TARGET || "production";
  const targetlessCommunityConfig = getTargetlessCommunityConfig(communityId);

  const {
    communityName,
    communitySailthruSlug,
    domain: communityCanonicalDomain,
    vipDomain,
    wpSiteId,
    communityShortName,
    isPrimaryCommunity,
    cycloneCommunities,
    primaryCommunityId,
    isFeedOnlyCommunity,
  } = targetlessCommunityConfig;

  // Handle using Fastly proxy for the WP API
  const fastlyProxyEnabled = process.env.ENABLE_FASTLY_API_PROXY === "true";
  const fastlyProxySubdomain =
    fastlyProxyEnabled && process.env.WP_API_PROXY_SUBDOMAIN?.length
      ? process.env.WP_API_PROXY_SUBDOMAIN
      : null;

  const effectiveVipDomain =
    isFeedOnlyCommunity && primaryCommunityId
      ? getTargetlessCommunityConfig(primaryCommunityId).vipDomain
      : vipDomain;

  const { vipCommunityDomain: communityDomain, vipCommunityApiDomain } =
    getVipDomains({
      targetEnv,
      vipDomain: effectiveVipDomain,
      fastlyProxySubdomain,
      isPrimaryCommunity,
    });

  const communityOrigin = `https://${communityDomain}`;

  // Sites with SSO (i.e. sbnation) will redirect to this auth domain for login/signup
  const ssoAuthDomain = process.env.NEXT_PUBLIC_IDENTITY_SSO_AUTH_DOMAIN || "";

  /**
   * NOTE: These variables are only used for the WP API.
   */
  const communityApiOrigin = `https://${vipCommunityApiDomain}`;
  const communityApiUrl = `${communityApiOrigin}${
    fastlyProxyEnabled ? "/wp-api" : ""
  }/graphql`;

  /**
   * Get primary community API origin from the primary community ID.
   * If this is the primary community, use its own API origin.
   * Otherwise, recursively get the primary community's API origin.
   */
  const primaryCommunityApiOrigin = isPrimaryCommunity
    ? communityApiOrigin
    : getCommunityConfig(primaryCommunityId).communityApiOrigin;

  const communityConfig: CommunityConfig = {
    communityApiOrigin,
    communityApiUrl,
    communityCanonicalDomain,
    communityDomain,
    communityOrigin,
    communityId,
    communityName,
    communitySailthruSlug,
    communityShortName,
    communityWpSiteId: wpSiteId,
    isPrimaryCommunity,
    primaryCommunityId,
    primaryCommunityApiOrigin,
    // siteOrigin is determined dynamically at runtime using siteDomain from WP site settings
    siteOrigin: null,
    vmPrefix: getVoxMediaCachePrefix(wpSiteId),
    voxCreativeCommunityId: VOX_CREATIVE_COMMUNITY_ID,
    ssoAuthDomain,
    /**
     * TODO: Not really sure we need this on community config. Need to go back and review.
     */
    cycloneCommunities,
    isFeedOnlyCommunity,
  };

  return communityConfig;
};
